/**
 * Coupon box for the plans page and the signup plan step (#943, #956).
 *
 * Presentational only: the page owns the code, checks it with the API and
 * re-prices its cards. This shows the three outcomes the same way on both
 * pages: applied (a green chip with Remove), not valid (a red line tied to
 * the input) and "try again later" (rate limited or unavailable, an amber
 * line, since nothing is wrong with what was typed). Messages are announced
 * through `aria-live`.
 */
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { PromoCode, PromotionCodeRefusal } from '@core/config/promotions';

@Component({
  selector: 'app-promo-code-box',
  templateUrl: './promo-code-box.component.html',
  styleUrl: './promo-code-box.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PromoCodeBoxComponent {
  /** Unique id for the input; the message element uses `<id>-message`. */
  @Input() inputId = 'sn-promo-input';

  /** What is in the box. */
  @Input() code = '';

  /** The applied promotion, or null. */
  @Input() applied: PromoCode | null = null;

  /** Why the last code was not applied, or null. */
  @Input() error: string | null = null;

  /** What kind of refusal `error` is; decides its colour and icon. */
  @Input() errorKind: PromotionCodeRefusal | null = null;

  /** True while the API is checking a code. */
  @Input() checking = false;

  /** The text in the box changed. */
  @Output() readonly codeChange = new EventEmitter<string>();

  /** Apply was pressed (or Enter in the box). */
  @Output() readonly apply = new EventEmitter<void>();

  /** Remove was pressed on the applied chip. */
  @Output() readonly remove = new EventEmitter<void>();

  /** True when the refusal is about timing, not the code itself. */
  get isRetryLater(): boolean {
    return this.errorKind === 'rate_limited' || this.errorKind === 'unavailable';
  }

  /** Submits the box without reloading the page. */
  submit(event: Event): void {
    event.preventDefault();
    if (!this.code.trim() || this.checking) return;
    this.apply.emit();
  }

  /** Forwards typing to the page. */
  onInput(event: Event): void {
    this.codeChange.emit((event.target as HTMLInputElement).value);
  }
}
